import { useCallback, useContext } from 'react';

import { BETA_FEATURES, type BetaFeatureKey } from '@/constants/betaFeatures';
import AppContext from '@/contexts/appContext';
import useUserSetting from '@/hooks/useUserSetting';
import type { BetaFeatureChoice, BetaStage } from '@/types/betaFeature';

const BETA_STAGES: readonly BetaStage[] = ['hidden', 'opt_in', 'opt_out', 'released'];

/** A missing or unrecognized Config reads as hidden, so a bad value can only hide a beta. */
export const toBetaStage = (value: unknown): BetaStage =>
  BETA_STAGES.includes(value as BetaStage) ? (value as BetaStage) : 'hidden';

export interface BetaFeatureResolution {
  /** Whether the user gets a switch for this beta. */
  isToggleVisible: boolean;
  /** Whether the user sees the beta UI. */
  isEnabled: boolean;
}

/**
 * A choice only counts in the stage it was made in. Moving a beta from opt_in
 * to opt_out therefore puts everyone on the beta, including anyone who tried it
 * and switched back, and they can switch back again.
 */
export const resolveBetaFeature = (
  stage: BetaStage,
  choice: BetaFeatureChoice | undefined
): BetaFeatureResolution => {
  const madeNow = choice?.stage === stage ? choice : undefined;

  switch (stage) {
    case 'opt_in':
      return { isToggleVisible: true, isEnabled: madeNow?.enabled ?? false };
    case 'opt_out':
      return { isToggleVisible: true, isEnabled: madeNow?.enabled ?? true };
    case 'released':
      return { isToggleVisible: false, isEnabled: true };
    default:
      return { isToggleVisible: false, isEnabled: false };
  }
};

export interface BetaFeatureState extends BetaFeatureResolution {
  stage: BetaStage;
  setEnabled: (enabled: boolean) => void;
}

const useBetaFeature = (key: BetaFeatureKey): BetaFeatureState => {
  const { config } = useContext(AppContext);
  const [choices, setChoices] = useUserSetting('betaFeatures', {});
  const stage = toBetaStage(config?.[BETA_FEATURES[key].configKey]);

  const setEnabled = useCallback(
    (enabled: boolean) => setChoices({ ...choices, [key]: { enabled, stage } }),
    [choices, key, setChoices, stage]
  );

  return { stage, ...resolveBetaFeature(stage, choices[key]), setEnabled };
};

export default useBetaFeature;
